import React from "react";
import { AlertTriangle } from "lucide-react";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import Table1 from "../../../../../../components/ACS-Dashboard/ACS-Blocks/TableSecondary";// adjust path if different


const ConfirmRecallModal = ({ 
  onClose, 
  onConfirm, 
  shipmentDetails, 
  title, 
  warningMessage, 
  confirmButtonMessage,
  requireReason = true,
  initialReason = "" ,
  isRemove = false
}) => {
  const {
    status,
    shipmentNumber,
    orderNumber,
    pickupDate,
    deliveryDate,
    portOfOrigin,
    portOfDestination,
    carrierName,
    carrierCode,
    totalMinutes,
    mode,
    currency = "USD",
    price,
  } = shipmentDetails || {};

  const [reason, setReason] = useState(initialReason);
  const maxLen = 255;

  useEffect(() => setReason(initialReason || ''), [initialReason]);

  // For Remove Orders flow
const [selectedOrders, setSelectedOrders] = useState([]);

const soList = (shipmentDetails?.salesOrderNumber || "")
  .split(",")
  .map((s) => s.trim())
  .filter(Boolean);

const poList = (shipmentDetails?.poNumber || "")
  .split(",")
  .map((s) => s.trim())
  .filter(Boolean);

const orderPairs = soList.map((so, i) => ({
  so,
  po: poList[i] || "-"
}));

const totalOrders = orderPairs.length;
const canSelectMore = selectedOrders.length < (totalOrders - 1);

const canToggle = (so) => {
  const isChecked = selectedOrders.includes(so);
  return isChecked || canSelectMore; // always allow uncheck; allow check only if not selecting all
};

const toggleOrder = (so) => {
  if (!canToggle(so)) return;
  setSelectedOrders((prev) =>
    prev.includes(so) ? prev.filter((o) => o !== so) : [...prev, so]
  );
};

  // build POs that correspond to selected SOs
  const selectedPOs = orderPairs
    .filter(p => selectedOrders.includes(p.so))
    .map(p => p.po)
    .filter(po => po && po !== "-");

  const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return `${(date.getMonth() + 1).toString().padStart(2, "0")}-${date
    .getDate()
    .toString()
    .padStart(2, "0")}-${date.getFullYear()}`;
};


  const handleConfirm = () => {
    if (!requireReason) return onConfirm?.();
    const trimmed = reason.trim();
    onConfirm?.(trimmed);
  };

  const confirmDisabled = requireReason && reason.trim().length === 0;

  return (
    <motion.div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black bg-opacity-30 px-fluid-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-2xl shadow-xl w-full max-w-3xl p-fluid-3 space-y-fluid-3"
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
      >
        {/* Header */}
        <div className="flex flex-col items-center space-y-fluid-1.5">
          <div className="bg-Delete-red rounded-full p-fluid-2 text-white">
            <AlertTriangle size={32} />
          </div>
          <h2 className="text-fluid-xl font-semibold text-[#111827]">
            {title || "Confirm Action"}
          </h2>
        </div>

        {/* Warning Banner */}
        <div className="bg-[#f8c1bc] border-l-4 border-[#b91c1c] px-fluid-2.5 py-fluid-2 rounded-md space-y-fluid-1">
          <div className="flex items-center text-[#111827] space-x-fluid-1">
            <AlertTriangle className="text-[#111827]" size={18} />
            <div className="text-fluid-base">
              <span className="font-bold">Warning</span>
              <span className="ml-fluid-0.5 font-medium">
                Are you sure you want to proceed?
              </span>
            </div>
          </div>
          <p className="text-[#111827] text-fluid-sm pl-[1.75rem]">
            {warningMessage || "This action cannot be undone."}
          </p>
        </div>

        {/* Shipment Info Grid */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-fluid-2.5 grid grid-cols-1 sm:grid-cols-2 gap-fluid-3 text-fluid-sm text-gray-800">
          {/* Left Section */}
          <div>
            <h4 className="font-bold mb-fluid-1">Shipment information</h4>
            <div className="space-y-fluid-0.5">
              <div><span className="font-medium">Status:</span> {status || "-"}</div>
              <div><span className="font-medium">Shipment date:</span> {formatDate(pickupDate)}</div>
              <div><span className="font-medium">Delivery date:</span> {formatDate(deliveryDate)}</div>
              <div><span className="font-medium">Shipment number:</span> #{shipmentNumber}</div>
              <div><span className="font-medium">Order value:</span> {orderNumber || "-"}</div>
            </div>
          </div>

          {/* Right Section */}
          <div>
            <h4 className="font-bold mb-fluid-1">Carrier confirmation</h4>
            <div className="space-y-fluid-0.5">
              <div><span className="font-medium">Origin:</span> {portOfOrigin || "-"}</div>
              <div><span className="font-medium">Destination:</span> {portOfDestination || "-"}</div>
              <div><span className="font-medium">Price:</span> {price ? `${price} ${currency}` : "-"}</div>
              <div><span className="font-medium">Transit Time:</span> {totalMinutes ? `${Math.round(totalMinutes / 1440)} days` : "-"}</div>
              <div><span className="font-medium">Carrier:</span> {carrierName || "-"}</div>
            </div>
          </div>
        </div>
        {/* === REMOVE ORDERS MODE === */}
{isRemove ? (
  <>
    <div className="max-h-72 overflow-y-auto mt-fluid-2">
      <Table1
        headers={["Select", "Sales Order #", "PO Number"]}
        columnWidths={["20%", "40%", "40%"]}
        data={orderPairs}
        renderRow={(row) => {
          const checked = selectedOrders.includes(row.so);
          return (
            <>
              <td className="px-fluid-3 py-fluid-2">
                <input
                  type="checkbox"
                  checked={checked}
                  aria-disabled={!canToggle(row.so)}
                  onChange={() => {
                    if (!canToggle(row.so)) return;
                    toggleOrder(row.so);
                  }}
                  title={!canToggle(row.so) ? "At least one order must remain on the shipment." : ""}
                  className={`h-4 w-4 accent-[#1e2a74] ${
                    !canToggle(row.so) ? "cursor-not-allowed opacity-60" : ""
                  }`}
                />
              </td>
              <td className="px-fluid-3 py-fluid-2 text-fluid-sm text-[#030229]">
                {row.so}
              </td>
              <td className="px-fluid-3 py-fluid-2 text-fluid-sm text-[#030229]">
                {row.po}
              </td>
            </>
          );
        }}
      />
    </div>

    {/* Footer Buttons */}
    <div className="flex justify-end gap-fluid-2 pt-fluid-2 border-t border-gray-200">
      <button
        onClick={onClose}
        className="px-fluid-button-x py-fluid-button-y border border-gray-300 rounded text-gray-700 hover:bg-gray-100 text-fluid-sm"
      >
        Cancel
      </button>
      <button
        onClick={() => onConfirm(selectedOrders, selectedPOs)}
        disabled={selectedOrders.length === 0 || selectedOrders.length === totalOrders}
        className={`px-fluid-button-x py-fluid-button-y text-white text-fluid-sm rounded font-semibold transition
          ${
            selectedOrders.length === 0 || selectedOrders.length === totalOrders
              ? "bg-gray-300 cursor-not-allowed"
              : "bg-[#1e2a74] hover:bg-[#18225c]"
          }`}
      >
        Remove Selected Orders
  </button>
    </div>
  </>
) : (
  <>
    {/* Reason input */}
    {requireReason && (
      <div className="space-y-fluid-1">
        <label className="block text-fluid-sm font-medium text-gray-800">
          Reason {requireReason && <span className="text-red-600">*</span>}
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value.slice(0, maxLen))}
          maxLength={maxLen}
          rows={3}
          placeholder="Add a brief reason (max 255 characters)…"
          className="w-full rounded-md border border-gray-300 px-fluid-2 py-fluid-1.5 text-fluid-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-300"
        />
        <div className="flex justify-between items-center text-fluid-xs text-gray-500">
          <span>{requireReason ? "Required" : "Optional"}</span>
          <span>{reason.length}/{maxLen}</span>
        </div>
      </div>
    )}

    {/* Action Buttons */}
    <div className="flex justify-end gap-fluid-2 pt-fluid-2 border-t border-gray-200">
      <button
        onClick={onClose}
        className="px-fluid-button-x py-fluid-button-y border border-gray-300 rounded text-gray-700 hover:bg-gray-100 text-fluid-sm transition"
      >
        Discard
      </button>
      <button
        onClick={handleConfirm}
        disabled={confirmDisabled}
        className={`px-fluid-button-x py-fluid-button-y text-white text-fluid-sm rounded font-semibold transition
          ${confirmDisabled ? "bg-gray-400 cursor-not-allowed" : "bg-Delete-red hover:bg-red-700"}`}
      >
        {confirmButtonMessage || "Confirm"}
      </button>
    </div>
  </>
)}


     
      </motion.div>
    </motion.div>
  );
};

export default ConfirmRecallModal;